import { supabase } from './authService';
import config from '../config';

/**
 * Migrate data from localStorage to Supabase
 * @param {string} userId - The user ID to associate the data with
 * @returns {Promise<Object>} - Migration result
 */
export const migrateToDatabase = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required for migration');
  }

  try {
    // Get data from localStorage
    const documents = JSON.parse(localStorage.getItem(config.storageKeys.documents) || '[]');
    const signatures = JSON.parse(localStorage.getItem(config.storageKeys.signatures) || '[]');
    const preferences = JSON.parse(localStorage.getItem(config.storageKeys.userPreferences) || '{}');
    
    console.log(`Found ${documents.length} documents, ${signatures.length} signatures in localStorage`);
    
    // Migrate documents
    const documentResults = [];
    for (const document of documents) {
      try {
        // Add user_id to document
        const documentWithUser = {
          ...document,
          user_id: userId
        };
        
        // Convert camelCase to snake_case for database fields
        if (documentWithUser.createdAt) {
          documentWithUser.created_at = documentWithUser.createdAt;
          delete documentWithUser.createdAt;
        }
        
        if (documentWithUser.updatedAt) {
          documentWithUser.updated_at = documentWithUser.updatedAt;
          delete documentWithUser.updatedAt;
        }
        
        if (documentWithUser.sentAt) {
          documentWithUser.sent_at = documentWithUser.sentAt;
          delete documentWithUser.sentAt;
        }
        
        if (documentWithUser.signedAt) {
          documentWithUser.signed_at = documentWithUser.signedAt;
          delete documentWithUser.signedAt;
        }
        
        if (documentWithUser.recipientEmail) {
          documentWithUser.recipient_email = documentWithUser.recipientEmail;
          delete documentWithUser.recipientEmail;
        }
        
        if (documentWithUser.signedData) {
          documentWithUser.signed_data = documentWithUser.signedData;
          delete documentWithUser.signedData;
        }
        
        // Insert document into Supabase
        const { data, error } = await supabase
          .from('documents')
          .upsert(documentWithUser)
          .select();
        
        if (error) {
          throw error;
        }
        
        documentResults.push({ id: document.id, success: true });
      } catch (err) {
        console.error(`Error migrating document ${document.id}:`, err);
        documentResults.push({ id: document.id, success: false, error: err.message });
      }
    }
    
    // Migrate signatures
    const signatureResults = [];
    for (const signature of signatures) {
      try {
        // Add user_id to signature
        const signatureWithUser = {
          ...signature,
          user_id: userId
        };
        
        // Convert camelCase to snake_case for database fields
        if (signatureWithUser.createdAt) {
          signatureWithUser.created_at = signatureWithUser.createdAt;
          delete signatureWithUser.createdAt;
        }
        
        // Insert signature into Supabase
        const { data, error } = await supabase
          .from('signatures')
          .upsert(signatureWithUser)
          .select();
        
        if (error) {
          throw error;
        }
        
        signatureResults.push({ id: signature.id, success: true });
      } catch (err) {
        console.error(`Error migrating signature ${signature.id}:`, err);
        signatureResults.push({ id: signature.id, success: false, error: err.message });
      }
    }
    
    // Migrate user preferences
    let preferencesResult = { success: true };
    try {
      // Update user profile with preferences
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ preferences })
        .eq('id', userId)
        .select();
      
      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Error migrating user preferences:', err);
      preferencesResult = { success: false, error: err.message };
    }
    
    // Calculate success rates
    const documentSuccessCount = documentResults.filter(r => r.success).length;
    const signatureSuccessCount = signatureResults.filter(r => r.success).length;
    
    return {
      success: true,
      migrated: {
        documents: {
          total: documents.length,
          success: documentSuccessCount,
          failed: documents.length - documentSuccessCount
        },
        signatures: {
          total: signatures.length,
          success: signatureSuccessCount,
          failed: signatures.length - signatureSuccessCount
        },
        preferences: preferencesResult
      }
    };
  } catch (error) {
    console.error('Migration error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if there is data in localStorage that can be migrated
 * @returns {Object} - Object with counts of items that can be migrated
 */
export const checkMigrationData = () => {
  try {
    const documents = JSON.parse(localStorage.getItem(config.storageKeys.documents) || '[]');
    const signatures = JSON.parse(localStorage.getItem(config.storageKeys.signatures) || '[]');
    const preferences = JSON.parse(localStorage.getItem(config.storageKeys.userPreferences) || '{}');
    
    return {
      hasData: documents.length > 0 || signatures.length > 0 || Object.keys(preferences).length > 0,
      counts: {
        documents: documents.length,
        signatures: signatures.length,
        hasPreferences: Object.keys(preferences).length > 0
      }
    };
  } catch (error) {
    console.error('Error checking migration data:', error);
    return {
      hasData: false,
      counts: {
        documents: 0,
        signatures: 0,
        hasPreferences: false
      }
    };
  }
};

export default {
  migrateToDatabase,
  checkMigrationData
};
